import { KeyboardEvent, useEffect, useRef, useState } from "react";

import { countryCodes } from "@Components/LeadFormModal/data/countryCodes";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useClickOutside from "hooks/useOutsideClick";
import styles from "./CountryCodeDropdown.module.scss";
import Input from "@Components/Input";
import DropdownIcon from "@Icons/DropdownIcon";
import getUserCountry from "@Pages/Search/components/SearchList/components/TrustPilotBanner/helper";
import { getCountryCallingCode } from "libphonenumber-js";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import config from "@Config/index";

const CountryCodeDropdown = ({
  value,
  label,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleOptionClick = () => {},
  onFocus,
  showOptions,
  handleClear,
  showErrorMessage,
  error,
  handleOutsideClick,
  dropdownContainerClassName = "",
  required = true,
  isActive = true,
  labelForLogic,
}: any) => {
  const dropdownRef = useRef(null);
  const [inpValue, setInpValue] = useState(value);
  const [countryCodesList, setCountryCodesList] = useState(countryCodes);

  const { storedCountryCode } = useSelector(
    ({ appData }: AppState) => ({
      storedCountryCode: appData.country,
    }),
    shallowEqual,
  );

  useEffect(() => setInpValue(value), [value]);

  const filterCountryCodes = (val: string) => {
    setInpValue(val);
    setCountryCodesList(
      countryCodes
        .filter((code) => code.value.toLocaleLowerCase().includes(val.toLocaleLowerCase()))
        .sort((a, b) => a.label?.length - b.label?.length),
    );
  };

  const handlKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && countryCodesList && countryCodesList.length > 0) {
      e.preventDefault();
      setInpValue(countryCodesList[0].label);
      handleOptionClick(countryCodesList[0].label);
      setTimeout(() => handleOutsideClick(), 200);
    }
  };

  useClickOutside(dropdownRef, () => {
    const isSelectedValuePresent = countryCodesList.find((code) => code.label === value);
    setCountryCodesList(countryCodes);

    if (inpValue && inpValue !== value && showOptions === label) {
      setInpValue(value);
      handleOptionClick(value);
    } else if (!isSelectedValuePresent && showOptions === label) {
      handleOptionClick("");
    }

    if (typeof handleOutsideClick !== undefined && showOptions === label) handleOutsideClick();
  });

  const handleSetCountryCode = async () => {
    const { country: userCountry } = await getUserCountry();
    const phoneNumberCountryCode = getCountryCallingCode(userCountry);
    setInpValue(`+${phoneNumberCountryCode}`);
    handleOptionClick(`+${phoneNumberCountryCode}`);
  };

  useEffect(() => {
    // For China, skip the userCountry lookup entirely
    if (config.IS_CHINA_APP) return;
    // added isActive check to call country api only where needed
    if (storedCountryCode && !value && !inpValue && isActive) handleSetCountryCode();
  }, [storedCountryCode, isActive]);
  const { t } = useTranslationHook();

  return (
    <div
      className={formatClassNames(styles.dropdownContainer, dropdownContainerClassName)}
      ref={dropdownRef}
    >
      <Input
        dataTestId="generic-leadform-input-countryCode"
        type="dropdown"
        label={t(label)}
        placeholder="Select"
        value={inpValue}
        onChange={filterCountryCodes}
        onFocus={(e) => {
          onFocus(e);
          setCountryCodesList(countryCodes);
        }}
        clearable
        handleClear={() => {
          setInpValue("");
          handleClear();
        }}
        onKeyDown={handlKeyDown}
        // rightIconClassName={errors.code ? styles.rightIconWithError : ""}
        rightIcon={!inpValue && <DropdownIcon size={15} />}
        showErrorMessage={showErrorMessage}
        error={error}
        required={required}
      />
      {countryCodesList.length === 0 && (
        <div className={formatClassNames(styles.options_container, styles.no_results)}>
          No Results
        </div>
      )}
      {(showOptions === label || showOptions === labelForLogic) && countryCodesList.length > 0 && (
        <div className={styles.options_container}>
          {countryCodesList.map((code, idx) => (
            <div
              role="none"
              className={formatClassNames(
                styles.option,
                value === code.label ? styles.selected : "",
              )}
              key={idx}
              onClick={() => {
                handleOptionClick(code.label);
                setTimeout(handleOutsideClick, 20);
              }}
            >
              <div>{code.label}</div>
              <div className={styles.optionDesc}>{code.description}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default wrapperHOC(CountryCodeDropdown, {
  componentName: "CountryCodeDropdown-BookForm",
  showForChina: true,
});
